import { store } from "../store";
import DataStructure, { sanitize } from "./DataStructure";
import DocumentRef from "./DocumentRef";
import Total from "./QuoteTotal";
import { deepFreeze, timestampToDate } from "../util/misc";
import { compact, isEmpty, isNumber, zipObject } from "lodash";

class Item extends DataStructure {
  constructor(data = {}, approved = false) {
    super(sanitize(data, { approved, changes: {} }));
  }

  get changes() {
    return new Changes(this.data.changes);
  }
}

export class Changes extends DataStructure {
  constructor(data) {
    super(sanitize(data, { quantity: null, value: null, other: "" }));
  }

  clean(item, itemValue) {
    if (this.data.value && this.data.value.trim) {
      this.data.value = this.data.value.trim();
    }
    this.data.other = this.data.other.trim();
    return this.data.other ||
      (itemValue !== null &&
        this.data.value &&
        this.data.value !== itemValue) ||
      (item &&
        this.data.quantity !== null &&
        this.data.quantity !== "" &&
        this.data.quantity !== item.quantity)
      ? this.data
      : {};
  }

  get isEmpty() {
    return (
      isEmpty(this.data) ||
      (!this.data.value && !this.data.other && !isNumber(this.data.quantity))
    );
  }
}

export const CUSTOMER_QUOTE_RESPONSE_FIELDS = {
  isApproved: false,
  changes: [],
  items: {},
  total: null,
  pdf: null,
  declinedReason: null,
  signature: null,
  dateRequired: null,
  expectCustomerContract: false,
  preapproved: false,
  nonfinal: false,
  update: false,

  // Meta info
  submitted: null,
  createdBy: null,
};
deepFreeze(CUSTOMER_QUOTE_RESPONSE_FIELDS);

export default class CustomerQuoteResponse extends DataStructure {
  constructor(customerID, quoteID, id, data = {}) {
    // Defaults

    const quote = store.state.main.customerQuotes[quoteID];
    if (quote && !data.submitted) {
      data.preapproved = quote.data.settings.preapproved || false;
      data.nonfinal = quote.data.settings.nonfinal || false;
    }
    if (data.approved === 1 && !data.isApproved) {
      data.isApproved = true;
    }

    data = sanitize(data, CUSTOMER_QUOTE_RESPONSE_FIELDS);

    // Convert to Date
    data.submitted = timestampToDate(data.submitted);

    super(data);

    this.customerID = customerID;
    this.quoteID = quoteID;
    this.id = id;

    // Items
    ["billTo", "shipTo", "deliveryContact", "total", "terms"].forEach((id) =>
      this.addItem(id)
    );

    if (quote) {
      quote.data.items.forEach((item) => this.addItem(item.id));
    }
    if (this.nonfinal) {
      this.approved = this.data.approved || 0;
    }

    // Total
    this.data.total = this.total.data;
  }

  // Database

  get path() {
    return CustomerQuoteResponse.getPath(
      this.customerID,
      this.quoteID,
      this.id
    );
  }

  get stateID() {
    return this.quoteID + this.id;
  }

  static getStateID(path) {
    const match = path.match(/^c\/[^/]+\/quotes\/([^/]+)\/responses\/([^/]+)$/);
    if (match) {
      return match[1] + match[2];
    }
    return null;
  }

  static getPath(customerID, quoteID, id) {
    let path = `c/${customerID}/quotes/${quoteID}/responses`;
    if (id) {
      path += `/${id}`;
    }
    return path;
  }

  static toFirestore(quoteResponse) {
    let output = { ...quoteResponse.data };
    if (output.declinedReason) {
      output.isApproved = false;
      output.changes = [];
      output.items = {};
      output.total = {};
    } else if (output.nonfinal) {
      output.isApproved = true;
      output.changes = [];
      output.items = {};
      output.total = {};
    } else if (output.update) {
      output.isApproved = true;
      output.changes = [];
    } else {
      output.isApproved = quoteResponse.percentApproved === 1;
      output.changes = quoteResponse.getChanges() || null;
    }
    return output;
  }

  static fromFirestore(snapshot, options) {
    const data = snapshot.data(options);
    return new CustomerQuoteResponse(
      snapshot.ref.parent.parent.parent.parent.id,
      snapshot.ref.parent.parent.id,
      snapshot.id,
      data
    );
  }

  // Editing

  addItem(id) {
    this.data.items[id] = new Item(
      this.data.items ? this.data.items[id] : undefined,
      this.data.preapproved
    ).data;
  }

  static cleanDocuments(data, removeUnsaved = false) {
    if (
      !data.pdf ||
      !new DocumentRef(data.pdf)[removeUnsaved ? "isSaved" : "isValid"]
    ) {
      data.pdf = null;
    }
    return data;
  }

  get length() {
    return Object.keys(this.data.items).length;
  }

  get percentApproved() {
    const total = this.length;
    const approved = Object.values(this.data.items).filter(
      (item) => item.approved
    ).length;
    return approved / total;
  }

  get percentComplete() {
    const total = this.length;
    const complete = Object.values(this.data.items).filter(
      (item) => item.approved || !isEmpty(item.changes)
    ).length;
    return complete / total;
  }

  getChanges() {
    const changes = [];
    Object.keys(this.data.items).forEach((id) => {
      const itemChanges = this.data.items[id].changes;
      if (!isEmpty(itemChanges)) {
        const itemIndex = this.quote.data.items.findIndex(
          (item) => item.id === id
        );
        if (itemIndex >= 0) {
          const item = this.quote.data.items[itemIndex];
          id = itemIndex + 1;
          if (isNumber(itemChanges.quantity)) {
            changes.push({
              id,
              type: "quantity",
              before: item.quantity,
              after: itemChanges.quantity,
              comment: itemChanges.other,
            });
          } else if (itemChanges.other) {
            changes.push({
              id,
              type: "other",
              before: "",
              after: itemChanges.other,
            });
          }
        } else if (itemChanges.value) {
          let before = this.quote.data[id];
          if (id === "billTo") {
            before = this.quote.billTo.address.toString();
            changes.push({
              id,
              type: "address",
              before,
              after: itemChanges.value,
            });
          } else if (id === "shipTo") {
            before = this.quote.shipTo.address.toString();
            changes.push({
              id,
              type: "address",
              before,
              after: itemChanges.value,
            });
          } else if (id === "deliveryContact") {
            before = compact([
              this.quote.deliveryContact.name,
              this.quote.deliveryContact.emails[0]
                ? this.quote.deliveryContact.emails[0].toString()
                : null,
              this.quote.deliveryContact.phones[0]
                ? this.quote.deliveryContact.phones[0].toString()
                : null,
            ]).join("\n");
            changes.push({
              id,
              type: "contact",
              before,
              after: itemChanges.value,
            });
          }
        } else if (itemChanges.other) {
          changes.push({
            id,
            type: "other",
            before: "",
            after: itemChanges.other,
          });
        }
      }
    });
    changes.sort((a, b) => {
      const aIsNumber = isNumber(a.id);
      const bIsNumber = isNumber(b.id);
      if (aIsNumber === bIsNumber) {
        if (aIsNumber) {
          return a.id - b.id;
        } else {
          return a.id < b.id ? -1 : 1;
        }
      } else {
        return aIsNumber ? 1 : -1;
      }
    });
    return changes;
  }

  // Formatting
  get quote() {
    return store.state.main.customerQuotes[this.quoteID];
  }

  get total() {
    return new Total(this.data.total);
  }

  get pdf() {
    return new DocumentRef(this.data.pdf);
  }

  get items() {
    return zipObject(
      Object.keys(this.data.items),
      Object.values(this.data.items).map(
        (item) => new Item(item, this.data.preapproved)
      )
    );
  }

  get createdBy() {
    return this.data.createdBy && store.state?.main?.users
      ? store.state.main.users[this.data.createdBy] || this.data.createdBy
      : null;
  }
}
